import React from 'react';
import {
  Grid, TextField, Typography, Box, MenuItem,
} from '@mui/material';
import { tipoViaRequiereNumero, getMaxLenVia, buildDireccionCO } from './EnvioNacionalModal.utils';
import type { TipoVia } from './EnvioNacionalModal.utils';

export type { TipoVia } from './EnvioNacionalModal.utils';

export type DireccionCOTouched = Partial<Record<'tipoVia' | 'via' | 'numeroVia' | 'detalleDireccion', boolean>>;

interface Props {
  tipoVia: string;
  via: string;
  numeroVia: string;
  detalleDireccion: string;
  tiposVia: TipoVia[];
  touched: DireccionCOTouched;
  onChangeTipoVia: (v: string) => void;
  onChangeVia: (v: string) => void;
  onChangeNumeroVia: (v: string) => void;
  onChangeDetalle: (v: string) => void;
  onBlur: (field: keyof DireccionCOTouched) => void;
}

const ALPHANUMERIC_VIA = /[^a-zA-Z0-9\s.áéíóúÁÉÍÓÚñÑüÜ-]/g;

const compactFieldSx = {
  '& .MuiFormHelperText-root': { mt: 0.75, mx: 0, minHeight: '1.1em', lineHeight: 1.2 },
};

const errorVia = (touched: boolean, via: string, maxVia: number): string => {
  if (!touched) return '';
  if (!via) return 'Campo requerido';
  return via.length > maxVia ? `Máximo ${maxVia} caracteres` : '';
};

const errorNumero = (touched: boolean, conNumero: boolean, numeroVia: string): string => {
  if (!touched) return '';
  if (conNumero && !numeroVia) return 'Campo requerido';
  return numeroVia.length > 15 ? 'Máximo 15 caracteres' : '';
};

export const DireccionColombiaForm: React.FC<Props> = ({
  tipoVia, via, numeroVia, detalleDireccion,
  tiposVia, touched,
  onChangeTipoVia, onChangeVia, onChangeNumeroVia, onChangeDetalle, onBlur,
}) => {
  const conNumero = tipoViaRequiereNumero(tipoVia, tiposVia);
  const maxVia = getMaxLenVia(tipoVia, tiposVia);
  const preview = buildDireccionCO(tipoVia, via, numeroVia, tiposVia);

  // Tres campos obligatorios para habilitar el detalle
  const detalleHabilitado = !!tipoVia && !!via && (!conNumero || !!numeroVia);

  const viaError = errorVia(!!touched.via, via, maxVia);
  const numeroError = errorNumero(!!touched.numeroVia, conNumero, numeroVia);

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary"
        sx={{ mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        Dirección
      </Typography>

      <Grid container spacing={1} alignItems="flex-start">
        <Grid size={{ xs: conNumero ? 4 : 6 }}>
          <TextField
            select
            fullWidth required
            sx={compactFieldSx}
            label="Tipo de vía"
            value={tipoVia}
            onChange={e => onChangeTipoVia(e.target.value)}
            onBlur={() => onBlur('tipoVia')}
            error={touched.tipoVia && !tipoVia}
            helperText={touched.tipoVia && !tipoVia ? 'Campo requerido' : ' '}
            size="small"
          >
            {tiposVia.map(t => (
              <MenuItem key={t.id} value={t.descripcion}>{t.descripcion}</MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid size={{ xs: conNumero ? 4 : 6 }}>
          <TextField
            fullWidth required
            sx={compactFieldSx}
            label={tipoVia || 'Vía'}
            value={via}
            onChange={e => onChangeVia(e.target.value.replaceAll(ALPHANUMERIC_VIA, '').slice(0, maxVia))}
            onBlur={() => onBlur('via')}
            error={!!viaError}
            helperText={viaError || ' '}
            size="small"
            slotProps={{
              htmlInput: { maxLength: maxVia },
            }}
          />
        </Grid>

        {conNumero && (
          <Grid size={{ xs: 4 }}>
            <TextField
              fullWidth required
              sx={compactFieldSx}
              label="Número"
              value={numeroVia}
              onChange={e => onChangeNumeroVia(e.target.value.replaceAll(ALPHANUMERIC_VIA, '').slice(0, 15))}
              onBlur={() => onBlur('numeroVia')}
              error={!!numeroError}
              helperText={numeroError || ' '}
              size="small"
              slotProps={{
                htmlInput: { maxLength: 15 },
              }}
            />
          </Grid>
        )}
      </Grid>

      {preview && (
        <Typography variant="body2" sx={{ mt: 0.25, mb: 1, color: 'text.secondary' }}>
          <strong>Dirección:</strong> {preview}
        </Typography>
      )}

      <TextField
        fullWidth
        sx={{ ...compactFieldSx, mt: preview ? 0 : 0.5 }}
        label="Apartamento, bloque, edificio, indicaciones"
        value={detalleDireccion}
        onChange={e => onChangeDetalle(e.target.value.slice(0, 200))}
        onBlur={() => onBlur('detalleDireccion')}
        disabled={!detalleHabilitado}
        helperText={detalleHabilitado ? `${detalleDireccion.length}/200` : undefined}
        size="small"
        slotProps={{ htmlInput: { maxLength: 200 } }}
      />
    </Box>
  );
};
